import {
  GROCERY_CATEGORIES,
  type GroceryCategory,
  type Ingredient,
  type IngredientListQuery,
} from '@recipes/contracts';
import { normalizeIngredientName } from '@recipes/domain';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { Plus, ScanBarcode, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { syncCatalog } from '@/features/catalog/device-copy';
import { useCatalogBoot } from '@/features/catalog/hooks';
import { groceryCategoryLabel } from '@/features/shared/labels';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { offlineDb } from '@/lib/offline/db';
import { queryKeys } from '@/lib/query-keys';
import { CatalogTabs, PendingNotice } from './catalog-pages';

const ALL = '__all__';
const PAGE_SIZE = 50;

type Scope = IngredientListQuery['scope'];
type HintFilter = NonNullable<IngredientListQuery['hints']>;
type NutritionFilter = NonNullable<IngredientListQuery['nutrition']>;

export interface IngredientFilters {
  q: string;
  category: GroceryCategory | null;
  scope: Scope;
  hints: HintFilter | null;
  nutrition: NutritionFilter | null;
}

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | null =>
  allowed.includes(value as T) ? (value as T) : null;

/** The filters live in the URL, so a filtered list can be bookmarked and survives "back". */
export function readIngredientFilters(params: URLSearchParams): IngredientFilters {
  return {
    q: params.get('q') ?? '',
    category: oneOf(params.get('category'), GROCERY_CATEGORIES),
    scope: oneOf(params.get('scope'), ['all', 'own', 'global'] as const) ?? 'all',
    hints: oneOf(params.get('hints'), ['any', 'recommended', 'avoid'] as const),
    nutrition: oneOf(params.get('nutrition'), ['with', 'without'] as const),
  };
}

/** The same filters against the device copy – what the list shows without a connection. */
export function filterIngredientsLocally(
  ingredients: Ingredient[],
  filters: IngredientFilters,
  hinted: { any: Set<string>; recommended: Set<string>; avoid: Set<string> },
): Ingredient[] {
  const needle = normalizeIngredientName(filters.q) || filters.q.trim().toLowerCase();
  return ingredients
    .filter((i) => {
      if (filters.scope === 'own' && i.scope === 'GLOBAL') return false;
      if (filters.scope === 'global' && i.scope !== 'GLOBAL') return false;
      if (filters.category && i.groceryCategory !== filters.category) return false;
      if (filters.hints && !hinted[filters.hints].has(i.id)) return false;
      if (filters.nutrition === 'with' && !i.nutrition) return false;
      if (filters.nutrition === 'without' && i.nutrition) return false;
      if (!needle) return true;
      return (
        i.normalizedName.includes(needle) ||
        i.aliases.some((a) => normalizeIngredientName(a.name).includes(needle))
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * All ingredients the user can see – the global set, the household's and their own – with
 * search and filters. Pages are loaded as the user scrolls; offline the device copy answers.
 */
export function CatalogIngredientsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const ids = { q: useId(), name: useId(), category: useId() };
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readIngredientFilters(params), [params]);
  const [q, setQ] = useState(filters.q);
  const debounced = useDebouncedValue(q.trim(), 250);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<GroceryCategory | typeof ALL>(ALL);
  useCatalogBoot();

  const setFilter = (key: keyof IngredientFilters, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value && value !== ALL && !(key === 'scope' && value === 'all')) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  // The search field writes to the URL once the user pauses; "back" restores the field.
  useEffect(() => {
    if (debounced !== filters.q) setFilter('q', debounced || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  useEffect(() => setQ(filters.q), [filters.q]);

  const activeFilters =
    Number(Boolean(filters.category)) +
    Number(filters.scope !== 'all') +
    Number(Boolean(filters.hints)) +
    Number(Boolean(filters.nutrition));

  const query = {
    q: filters.q || undefined,
    category: filters.category ?? undefined,
    scope: filters.scope,
    hints: filters.hints ?? undefined,
    nutrition: filters.nutrition ?? undefined,
  };
  const list = useInfiniteQuery({
    queryKey: queryKeys.catalog.ingredients(query),
    queryFn: ({ pageParam }) =>
      api.ingredients.list({ ...query, limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: online,
  });

  // Offline the device copy answers: the household's ingredients and the global set.
  const local = useLiveQuery(async () => {
    const [rows, hints] = await Promise.all([
      offlineDb.catalogIngredients.toArray(),
      offlineDb.catalogHints.toArray(),
    ]);
    const hinted = {
      any: new Set<string>(),
      recommended: new Set<string>(),
      avoid: new Set<string>(),
    };
    for (const { hint } of hints) {
      if (!hint.ingredientId) continue;
      hinted.any.add(hint.ingredientId);
      if (hint.verdict === 'RECOMMENDED') hinted.recommended.add(hint.ingredientId);
      if (hint.verdict === 'AVOID') hinted.avoid.add(hint.ingredientId);
    }
    return { ingredients: rows.map((r) => r.ingredient), hinted };
  }, []);
  const offlineItems = useMemo(
    () => (local ? filterIngredientsLocally(local.ingredients, filters, local.hinted) : []),
    [local, filters],
  );

  const items = online ? (list.data?.pages.flatMap((p) => p.items) ?? []) : offlineItems;
  const total = online ? (list.data?.pages[0]?.total ?? null) : offlineItems.length;

  // Load the next page when the end of the list comes into view; the button stays as a fallback.
  const sentinel = useRef<HTMLDivElement | null>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  const unlinked = useQuery({
    queryKey: queryKeys.catalog.unlinked,
    queryFn: () => api.ingredients.unlinked(),
    enabled: online,
  });

  const create = useMutation({
    mutationFn: (input: { name: string; category?: GroceryCategory }) =>
      api.ingredients.create({ name: input.name, groceryCategory: input.category }),
    onSuccess: (ingredient) => {
      toast.success(t('catalog.ingredients.created'));
      setCreateOpen(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all });
      void syncCatalog();
      navigate(`/catalog/ingredients/${ingredient.id}`);
    },
    onError: (e) => toastError(e),
  });

  const filtered = Boolean(filters.q) || activeFilters > 0;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('catalog.title')}
        description={t('catalog.description')}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/catalog/scan">
                <ScanBarcode aria-hidden /> {t('catalog.scan.title')}
              </Link>
            </Button>
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={!online}
              data-testid="ingredient-new"
            >
              <Plus aria-hidden /> {t('catalog.ingredients.new')}
            </Button>
          </>
        }
      />
      <CatalogTabs />
      <PendingNotice />

      <form
        role="search"
        aria-label={t('catalog.ingredients.filters')}
        className="mb-3 space-y-2"
        onSubmit={(e) => e.preventDefault()}
      >
        <div>
          <Label htmlFor={ids.q}>{t('common.search')}</Label>
          <Input
            id={ids.q}
            className="mt-1"
            type="search"
            value={q}
            placeholder={t('catalog.ingredients.searchPlaceholder')}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ingredient-search"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Select value={filters.category ?? ALL} onValueChange={(v) => setFilter('category', v)}>
            <SelectTrigger
              aria-label={t('shopping.item.category')}
              data-testid="ingredient-filter-category"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('catalog.ingredients.categoryAll')}</SelectItem>
              {GROCERY_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {groceryCategoryLabel(t, c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.scope} onValueChange={(v) => setFilter('scope', v)}>
            <SelectTrigger
              aria-label={t('catalog.ingredients.scope')}
              data-testid="ingredient-filter-scope"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('catalog.ingredients.scopeAll')}</SelectItem>
              <SelectItem value="own">{t('catalog.ingredients.scopeOwn')}</SelectItem>
              <SelectItem value="global">{t('catalog.ingredients.scopeGlobal')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.hints ?? ALL} onValueChange={(v) => setFilter('hints', v)}>
            <SelectTrigger
              aria-label={t('catalog.ingredients.hints')}
              data-testid="ingredient-filter-hints"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('catalog.ingredients.hintsAll')}</SelectItem>
              <SelectItem value="any">{t('catalog.ingredients.hintsAny')}</SelectItem>
              <SelectItem value="recommended">
                {t('catalog.ingredients.hintsRecommended')}
              </SelectItem>
              <SelectItem value="avoid">{t('catalog.ingredients.hintsAvoid')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.nutrition ?? ALL} onValueChange={(v) => setFilter('nutrition', v)}>
            <SelectTrigger
              aria-label={t('catalog.ingredients.nutrition')}
              data-testid="ingredient-filter-nutrition"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('catalog.ingredients.nutritionAll')}</SelectItem>
              <SelectItem value="with">{t('catalog.ingredients.nutritionWith')}</SelectItem>
              <SelectItem value="without">{t('catalog.ingredients.nutritionWithout')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-h-[32px] flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground" role="status" data-testid="ingredient-count">
            {total === null
              ? ''
              : filtered
                ? t('catalog.ingredients.countFiltered', { count: total })
                : t('catalog.ingredients.count', { count: total })}
          </p>
          {filtered && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setQ('');
                setParams({}, { replace: true });
              }}
              data-testid="ingredient-filter-reset"
            >
              <X aria-hidden /> {t('catalog.ingredients.resetFilters')}
            </Button>
          )}
        </div>
      </form>

      {online && list.isLoading ? (
        <Spinner label={t('app.loading')} />
      ) : online && list.error ? (
        <Alert variant="destructive">{describeError(list.error)}</Alert>
      ) : items.length === 0 ? (
        <EmptyState
          title={
            filtered ? t('catalog.ingredients.noMatchTitle') : t('catalog.ingredients.emptyTitle')
          }
          description={
            !online && !local?.ingredients.length
              ? t('catalog.offlineNoCopy')
              : filtered
                ? t('catalog.ingredients.noMatchBody')
                : t('catalog.ingredients.emptyBody')
          }
        />
      ) : (
        <>
          <ul className="divide-y rounded-lg border" data-testid="ingredient-list">
            {items.map((ingredient) => (
              <IngredientRow key={ingredient.id} ingredient={ingredient} />
            ))}
          </ul>
          <div ref={sentinel} aria-hidden />
          {online && hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button
                variant="outline"
                loading={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
                data-testid="ingredient-more"
              >
                {t('catalog.ingredients.more')}
              </Button>
            </div>
          )}
        </>
      )}

      {online && (unlinked.data?.length ?? 0) > 0 && (
        <section className="mt-6" aria-labelledby="unlinked-title">
          <h2 id="unlinked-title" className="mb-1 font-semibold">
            {t('catalog.unlinked.title')}
          </h2>
          <p className="mb-2 text-sm text-muted-foreground">{t('catalog.unlinked.body')}</p>
          <ul className="divide-y rounded-lg border" data-testid="unlinked-list">
            {unlinked.data!.map((entry) => (
              <li key={entry.normalizedName} className="flex items-center gap-3 px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm">
                  {entry.name}{' '}
                  <span className="text-xs text-muted-foreground">
                    {t('catalog.unlinked.recipes', { count: entry.recipeCount })}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  loading={create.isPending && create.variables?.name === entry.name}
                  onClick={() => create.mutate({ name: entry.name })}
                >
                  {t('catalog.unlinked.add')}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('catalog.ingredients.new')}</DialogTitle>
          </DialogHeader>
          <form
            noValidate
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim())
                create.mutate({
                  name: name.trim(),
                  category: category === ALL ? undefined : category,
                });
            }}
          >
            <div>
              <Label htmlFor={ids.name}>{t('catalog.ingredients.name')}</Label>
              <Input
                id={ids.name}
                className="mt-1"
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="ingredient-name"
              />
            </div>
            <div>
              <Label htmlFor={ids.category}>{t('shopping.item.category')}</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as GroceryCategory)}>
                <SelectTrigger id={ids.category} className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('catalog.ingredients.categoryAuto')}</SelectItem>
                  {GROCERY_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {groceryCategoryLabel(t, c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                loading={create.isPending}
                disabled={!name.trim()}
                data-testid="ingredient-save"
              >
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function IngredientRow({ ingredient }: { ingredient: Ingredient }) {
  const { t } = useTranslation();
  const kcal = ingredient.nutrition?.energyKcal;
  const details = [
    groceryCategoryLabel(t, ingredient.groceryCategory),
    ingredient.aliases.length > 0
      ? ingredient.aliases
          .slice(0, 3)
          .map((a) => a.name)
          .join(', ')
      : null,
    kcal ? t('catalog.ingredients.kcal', { value: Math.round(Number(kcal)) }) : null,
    ingredient.productCount > 0
      ? t('catalog.ingredients.products', { count: ingredient.productCount })
      : null,
  ].filter(Boolean);
  return (
    <li data-testid="ingredient-row">
      <Link
        to={`/catalog/ingredients/${ingredient.id}`}
        className="flex min-h-[52px] items-center gap-3 px-3 py-2 hover:bg-accent/40"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{ingredient.name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {details.join(' · ')}
          </span>
        </span>
        {ingredient.hintSummary.avoid > 0 && (
          <Badge variant="destructive">
            {t('catalog.hint.avoidCount', { count: ingredient.hintSummary.avoid })}
          </Badge>
        )}
        {ingredient.hintSummary.recommended > 0 && (
          <Badge variant="success">
            {t('catalog.hint.recommendedCount', { count: ingredient.hintSummary.recommended })}
          </Badge>
        )}
        {ingredient.scope !== 'GLOBAL' && <Badge variant="muted">{t('catalog.own')}</Badge>}
      </Link>
    </li>
  );
}
